/* Copyright (c) 2025 Xperi. All Rights Reserved. */
/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */

export interface IMsoConfigurationData {
    SupportedLanguages?: string[];
    [key: string]: any;
}

export class MsoConfiguration {
    private static TAG = 'MsoConfiguration';

    private static configurationMap: IMsoConfigurationData = {};

    /**
     * Sets MSO configuration by loading and merging JSON files in the following order:
     * 1. Default configuration (any_any_any_unmanaged_smarttv.json)
     * 2. MSO-specific configuration without region (any_any_mso_unmanaged_smarttv.json)
     * 3. Region-specific configuration (any_region_any_unmanaged_smarttv.json)
     * 4. MSO-specific configuration with region (any_region_mso_unmanaged_smarttv.json)
     *
     * @param region - The region code (e.g., "us", "latam")
     * @param msoName - MSO name (e.g., "claro", "vtr", "llacr", "llapa", "llapadev", "clarodev")
     */
    public static setMsoConfiguration = (region?: string, msoName?: string) => {
        const regionLowerCase = region?.toLowerCase() || 'any';
        const msoNameLowerCase = msoName?.toLowerCase() || 'any';

        // Handle dev variations - remove 'dev' suffix for file lookup
        let msoForFile = msoNameLowerCase;
        if (msoNameLowerCase.endsWith('dev')) {
            msoForFile = msoNameLowerCase.replace(/dev$/, '');
        }

        let defaultConfiguration = {};
        let msoAnyRegionConfiguration = {};
        let regionConfiguration = {};
        let msoRegionConfiguration = {};

        // Load default configuration file
        try {
            defaultConfiguration = require('../../assets/msoconfigurations/any_any_any_unmanaged_smarttv.json');
        } catch (err) {
            // Do Nothing - use empty object as fallback
        }

        // Load MSO-specific configuration without region (any_any_mso)
        try {
            msoAnyRegionConfiguration = require(
                `../../assets/msoconfigurations/any_any_${msoForFile}_unmanaged_smarttv.json`,
            );
        } catch (err) {
            // Do Nothing - file might not exist
        }

        // Load region-specific configuration
        try {
            regionConfiguration = require(
                `../../assets/msoconfigurations/any_${regionLowerCase}_any_unmanaged_smarttv.json`,
            );
        } catch (err) {
            // Do Nothing - file might not exist
        }

        // Load MSO-specific configuration with region (any_region_mso)
        try {
            msoRegionConfiguration = require(
                `../../assets/msoconfigurations/any_${regionLowerCase}_${msoForFile}_unmanaged_smarttv.json`,
            );
        } catch (err) {
            // Do Nothing - file might not exist
        }

        // Reset configuration map first
        MsoConfiguration.configurationMap = {};

        // Merge configurations in order of specificity (least to most specific)
        MsoConfiguration.configurationMap = Object.assign(
            MsoConfiguration.configurationMap,
            defaultConfiguration,
        );
        MsoConfiguration.configurationMap = Object.assign(
            MsoConfiguration.configurationMap,
            msoAnyRegionConfiguration,
        );
        MsoConfiguration.configurationMap = Object.assign(
            MsoConfiguration.configurationMap,
            regionConfiguration,
        );
        MsoConfiguration.configurationMap = Object.assign(
            MsoConfiguration.configurationMap,
            msoRegionConfiguration,
        );
    };

    /**
     * Gets a configuration value by key
     * @param key - The configuration key to retrieve
     * @returns The configuration value or undefined if not found
     */
    public static getConfigurationValue = (key: string): any => {
        return MsoConfiguration.configurationMap[key];
    };

    /**
     * Gets the list of supported languages
     * @returns Array of supported language codes or default array
     */
    public static getSupportedLanguages = (): string[] => {
        return MsoConfiguration.configurationMap.SupportedLanguages || ['en', 'es'];
    };

    /**
     * Checks if a language is supported
     * @param languageCode - The language code to check
     * @returns True if the language is supported, false otherwise
     */
    public static isLanguageSupported = (languageCode: string): boolean => {
        const supportedLanguages = MsoConfiguration.getSupportedLanguages();
        return supportedLanguages.includes(languageCode.toLowerCase());
    };

    /**
     * Gets the entire configuration map
     * @returns The complete configuration object
     */
    public static getConfigurationMap = (): IMsoConfigurationData => {
        return { ...MsoConfiguration.configurationMap };
    };
}
